// src/views/admin/DigitalTwinPage.jsx
// --- UPDATE: v6.0 — simulation engine fully connected + performance pass
//   SIMULATION
//   • One shared simulation state (useSimulation) feeds the Tools panel, the 3D
//     scene, the data overlay, the asset modal and the twin drawer.
//   • SimulationImpact3DLayer draws damage markers, flood columns and the
//     hurricane track / eye / max-wind ring with hour-by-hour playback.
//   • Simulated states glow on the existing asset meshes (critical / warning).
//   • AI storm outlook chip (live Open-Meteo diagnostics) with one-click
//     "run flood forecast on twin"; broadcasts from other operators surface as
//     a banner that can be dismissed.
//   • "Fly to" from the results list centres the map and opens the asset.
//   FIXES
//   • Pump-failure override compared the scenario TITLE ('Critical Pump Failure')
//     to 'Pump Failure' so it never applied — and rebuilt the whole scene on
//     every scenario change. Replaced by the real simulation result.
//   • Predicted-failure markers compared string keys to numeric userData ids
//     with ===, so no marker was ever placed; lookups are now O(1) via a Map.
//   • Material overrides re-traversed every mesh on every telemetry flush; now
//     only objects whose status actually changed are touched.
//   • WebSocket handlers re-subscribed whenever links / simulation changed and
//     called updateLiveReadings inside a setState updater (runs twice in
//     StrictMode). Stable handlers via refs; side effect moved out.
//   • Health + telemetry hydration now run in parallel; bulk health primes the
//     AssetHealthIndicator cache; "Data Refreshed" toast only on manual refresh.
//   • Predicted-failure cones were sized/offset in metres but the twin's scene is
//     in Mercator units (≈1 unit = Earth's circumference) — now converted.
// --- END UPDATE
// --- UPDATE: v7.0 (Digital Twin MOW+ v1) — live data, FlowSense AI/ML, explainer, fixes
//   LIVE (WebSocket)
//   • Besides telemetry, the twin now follows the server's operational events:
//     asset_status_update (status changes applied in place), asset_prediction_update (ML health
//     re-fetched), critical_anomaly (server anomaly → AI findings + alert refresh),
//     ai_flood_prediction (well flood risk → AI findings), alert / issue / asset / pipeline /
//     sensor change events (targeted, debounced refresh — the 3D scene is not rebuilt for an alert).
//   • Live-feed indicator (socket state, messages per minute, last message). While the socket is
//     down, readings + alerts are re-fetched every 2 minutes (tab visible only).
//   • Historical replay: stale responses are dropped (only the latest request is applied).
//   FLOWSENSE AI / ML
//   • useTwinIntel: every sensor stream learns its normal level (EWMA); spikes, drops and stuck
//     sensors are flagged; drops on the same pipeline become suspected leaks; every asset gets an
//     explainable risk score (ML health + RUL + live + alerts + issues + anomalies).
//   • New sidebar tab "AI" (TwinAiExplainer): network health with every deduction, findings with
//     Fly to / Open sensor, risk ranking, Explain this screen, For the Ministry of Works,
//     Ask FlowSense (FlowSense AI engine, built-in rules when offline).
//   • The intel is passed down: Inspect panel (risk card, sensor ML badges, live history),
//     AssetHealthPanel (drivers + actions), Sensor Intelligence drawer, Layer counts, timeline
//     event markers, export, AR context.
//   FIXES
//   • Layer presets / Show-Hide All / the controls' layer menu: handleLayerToggle ignored the
//     requested value and read stale state, so presets flipped the wrong layers. It honours the
//     value and the scene groups follow the state.
//   • Pipeline3DLayer rebuilt every pipe on every telemetry flush (inline onLayerUpdated) — stable
//     callback; the layer stays mounted and is hidden instead of unmounted (pipes were left behind).
//   • Focus Mode button had no handler — hides the sidebar and overlays (F / Esc).
//   • SensorInfoDrawer existed but was never opened — "Sensor intelligence" from the Inspect panel,
//     the AI findings or the selected sensor.
//   • Reset View in the controls flew to pitch/bearing only — uses the twin's resetCamera.
// --- END UPDATE
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Box, Spinner, Flex, Text, Heading, VStack, Divider, IconButton,
  Tabs, TabList, TabPanels, Tab, TabPanel, Badge, HStack, useToast,
  Menu, MenuButton, MenuList, MenuItem, Tooltip, useColorModeValue,
  Icon, Button, useDisclosure, Drawer, DrawerOverlay, DrawerContent,
  useBreakpointValue, // --- UPDATE (twin v8)
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
  ChevronLeftIcon, ChevronRightIcon, SettingsIcon, DownloadIcon,
  ViewIcon, ViewOffIcon, RepeatIcon
} from '@chakra-ui/icons';
import { FaBrain, FaServer } from 'react-icons/fa';
import * as THREE from 'three';
import { motion, AnimatePresence } from 'framer-motion';

// Imports
import Anomalies3DLayer from '../../components/digital_twin/Anomalies3DLayer.js';
import Pipeline3DLayer from '../../components/digital_twin/Pipeline3DLayer.jsx';
import { useDigitalTwin } from '../../hooks/useDigitalTwin';
import AssetInformationPanel from '../../components/digital_twin/AssetInformationPanel.js';
import DataOverlay from '../../components/digital_twin/DataOverlay.js';
import DigitalTwinLayerManager from '../../components/digital_twin/DigitalTwinLayerManager.js';
import AssetDetailModal from '../../components/digital_twin/AssetDetailModal.js';
import SimulationPanel from '../../components/digital_twin/SimulationPanel.js';
import AugmentedRealityButton from '../../components/digital_twin/AugmentedRealityButton.js';
import TimeSlider from '../../components/digital_twin/TimeSlider.js';
import DigitalTwinControls from '../../components/digital_twin/DigitalTwinControls.js';
import DigitalTwinOverlay from '../../components/digital_twin/DigitalTwinOverlay.js';
// --- UPDATE
import SimulationImpact3DLayer from '../../components/digital_twin/SimulationImpact3DLayer.js';
import AiStormOutlook from '../../components/digital_twin/AiStormOutlook.js';
// --- UPDATE (elevation): rainfall + run-off overlay and ground elevation for the inspected asset
import RunoffFlow3DLayer from '../../components/digital_twin/RunoffFlow3DLayer.js';
import GroundElevationCard from '../../components/elevation/GroundElevationCard';
// --- END UPDATE
// --- UPDATE (twin v8): staged loading, terrain layer, live drainage outlook
import TwinLoadingOverlay from '../../components/digital_twin/TwinLoadingOverlay.js';
import TerrainElevation3DLayer, { TerrainLegend, terrainStats } from '../../components/digital_twin/TerrainElevation3DLayer.js';
import DrainageOutlookCard from '../../components/digital_twin/DrainageOutlookCard.js';
// --- END UPDATE
import { primeAssetHealthCache } from '../../components/digital_twin/assetHealthCache.js';
import { SCENARIO_BY_ID, defaultValuesFor } from '../../components/digital_twin/simulationScenarios.js';
import useSimulation from '../../hooks/useSimulation';
// --- END UPDATE

import * as SensorService from '../../services/sensorService.js';
import * as AssetService from '../../services/assetService.js';
import * as PipelineService from '../../services/pipelineService.js';
import * as IssueService from '../../services/issueService.js';
import * as PsiConditionService from '../../services/psiConditionService.js';
import * as ActiveAlertService from '../../services/activeSensorAlertService.js';
import * as PredictiveFailureService from '../../services/predictiveFailureService.js';
import webSocketService from '../../services/websocketService.js';
import { config } from '../../config';

import { getAuthToken } from '../../utils/auth';
// --- UPDATE (v7.0)
import SensorInfoDrawer from '../../components/digital_twin/SensorInfoDrawer.jsx';
import TwinAiExplainer from '../../components/digital_twin/TwinAiExplainer.js';
import TwinLiveIndicator from '../../components/digital_twin/TwinLiveIndicator.js';
import useTwinIntel from '../../hooks/useTwinIntel';
import { sensorKey as twinSensorKey, coords as twinCoords } from '../../components/digital_twin/twinIntel.js';
import { normalizePath } from '../../components/digital_twin/Pipeline3DLayer.jsx';
// --- END UPDATE
const API_BASE_URL = config.apiUrl.replace(/\/api\/v1$/, '');

const MotionFlex = motion(Flex);

// ============================================================================
// --- UPDATE: ENTERPRISE CONFIGURATION CONSTANTS ---
// Centralized tunables so fetch behaviour, retries, and thresholds are not
// scattered as magic numbers across the component body.
// ============================================================================
const FETCH_CONFIG = Object.freeze({
  HEALTH_BULK_CHUNK_SIZE: 250,      // Max asset IDs per bulk POST to avoid payload bloat
  READINGS_LIMIT: 300,              // Pressure reading hydration cap
  REQUEST_TIMEOUT_MS: 15000,        // Per-request abort ceiling
  MAX_RETRIES: 2,                   // Retry attempts for transient network failures
  RETRY_BASE_DELAY_MS: 400,         // Exponential backoff base
});

// --- UPDATE (v7.0): operational WebSocket events (see websocket.js) and what each refreshes
const TWIN_EVENTS = Object.freeze({
  alerts: ['new_alert', 'alert_created', 'alert_update', 'alert_updated', 'active_alert_update', 'sensor_alert', 'alert_resolved'],
  issues: ['new_issue', 'issue_created', 'issue_update', 'issue_updated'],
  health: ['asset_prediction_update', 'asset_health_update', 'prediction_update', 'predictive_failure_update', 'work_order_completed', 'work_order_update'],
  scene: ['asset_update', 'asset_created', 'asset_deleted', 'pipeline_update', 'pipeline_created', 'sensor_update', 'sensor_created', 'sensor_link_update'],
});
const TELEMETRY_EVENTS = Object.freeze(['new_pressure_reading', 'new_water_level_reading', 'psi_update', 'level_update', 'telemetry']);
const OFFLINE_POLL_MS = 120000;
const socketConnected = () => {
  try {
    const s = webSocketService;
    if (!s) return null;
    if (typeof s.isConnected === 'function') return !!s.isConnected();
    if (typeof s.isConnected === 'boolean') return s.isConnected;
    const sock = s.ws || s.socket;
    if (sock && typeof sock.readyState === 'number') return sock.readyState === 1;
  } catch (_) { /* unknown */ }
  return null;
};
const payloadOf = (m) => (m && (m.payload || m.data)) || m || {};
// --- END UPDATE

const HEALTH_STATUS = Object.freeze({
  CRITICAL: 'critical',
  WARNING: 'warning',
  NORMAL: 'normal',
});

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(128, 90, 213, 0.7); }
  70% { box-shadow: 0 0 0 6px rgba(128, 90, 213, 0); }
  100% { box-shadow: 0 0 0 0 rgba(128, 90, 213, 0); }
`;

const failureIconGeometry = new THREE.ConeGeometry(5, 15, 16);
const failureIconMaterial = new THREE.MeshStandardMaterial({
  color: 0xffa500,
  emissive: 0xffa500,
  emissiveIntensity: 0.8,
});

// ============================================================================
// --- UPDATE: NETWORK UTILITIES (module-scoped, pure, testable) ---
// fetchWithResilience wraps fetch with timeout, abort, and exponential-backoff
// retry for transient failures. Keeps the component body declarative.
// ============================================================================
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

async function fetchWithResilience(url, options = {}, {
  retries = FETCH_CONFIG.MAX_RETRIES,
  timeoutMs = FETCH_CONFIG.REQUEST_TIMEOUT_MS,
} = {}) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timer);
      // Retry only on transient server errors (5xx) and rate limiting (429)
      if ((response.status >= 500 || response.status === 429) && attempt < retries) {
        await sleep(FETCH_CONFIG.RETRY_BASE_DELAY_MS * Math.pow(2, attempt));
        continue;
      }
      return response;
    } catch (err) {
      clearTimeout(timer);
      lastError = err;
      // AbortError or network error -> backoff and retry if attempts remain
      if (attempt < retries) {
        await sleep(FETCH_CONFIG.RETRY_BASE_DELAY_MS * Math.pow(2, attempt));
        continue;
      }
    }
  }
  throw lastError || new Error(`Request failed after ${retries + 1} attempts: ${url}`);
}

// Chunk an array into fixed-size slices (for bulk health POST batching)
// --- UPDATE: stable empty history prop for the memoised twin drawer
const EMPTY_HISTORY = [];
// --- END UPDATE

function chunkArray(arr, size) {
  if (!Array.isArray(arr) || size <= 0) return [];
  const out = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

export default function DigitalTwinPage() {
  const containerRef = useRef(null);
  const toast = useToast();

  // ==================== STATE ====================
  const [isLoaded, setIsLoaded] = useState(false);
  const [fullDetailAsset, setFullDetailAsset] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAiMode, setIsAiMode] = useState(false);
  const [isDataOverlayMinimized, setIsDataOverlayMinimized] = useState(false);

  // Overlay Drawer State
  const { isOpen: isTwinDrawerOpen, onOpen: onTwinDrawerOpen, onClose: onTwinDrawerClose } = useDisclosure();
  const [twinOverlayAsset, setTwinOverlayAsset] = useState(null);

  // Data States
  const [assets, setAssets] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [sensorPipelineLinks, setSensorPipelineLinks] = useState({}); // Link Mapping
  const [issues, setIssues] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [psiConditions, setPsiConditions] = useState([]);
  const [sensorReadings, setSensorReadings] = useState({});
  const [assetHealthData, setAssetHealthData] = useState({});

  const [simulationMode, setSimulationMode] = useState(null);

  const [layerVisibility, setLayerVisibility] = useState({
    assets: true, pipelines: true, sensors: true, alerts: true,
    issues: true, openWorkOrders: true, predictedFailures: true, technicianLocations: true,
    terrain: false, // --- UPDATE (twin v8): TessaDEM ground elevation (off by default)
  });
  // --- UPDATE (twin v8): staged loading — engine → network data → ML health & telemetry → 3D scene
  const [loadStages, setLoadStages] = useState({ data: 'active', health: 'pending' });
  const [loadTimes, setLoadTimes] = useState({});
  const loadT0Ref = useRef(Date.now());
  // --- END UPDATE

  const [isLive, setIsLive] = useState(true);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);

  // --- UPDATE: surfaced fetch error state for the error/retry CTA pattern ---
  const [fetchError, setFetchError] = useState(null);

  // --- UPDATE: sidebar tab control (0 Inspect · 1 Layers · 2 Tools) + simulation layer toggle
  const [sidebarTab, setSidebarTab] = useState(0);
  const [showSimulationLayer, setShowSimulationLayer] = useState(true);
  const simulation = useSimulation();
  // --- END UPDATE

  // --- UPDATE (v7.0): focus mode, sensor drawer, server ML events, live-feed stats
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [drawerSensorKey, setDrawerSensorKey] = useState(null);
  const [floodPredictions, setFloodPredictions] = useState([]);
  const [serverAnomalies, setServerAnomalies] = useState([]);
  const [liveStats, setLiveStats] = useState({ connected: null, lastMessageAt: null, perMinute: null });
  const [sceneVersion, setSceneVersion] = useState(0);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const msgTimesRef = useRef([]);
  const historicalReqRef = useRef(0);
  const opQueueRef = useRef(new Set());
  const opTimerRef = useRef(null);
  const sceneTimerRef = useRef(null);
  const statusQueueRef = useRef(new Map());
  const statusTimerRef = useRef(null);
  const anomalyToastRef = useRef(new Map());
  const preFocusSidebarRef = useRef(true);
  // --- END UPDATE

  // ==================== THEME HOOKS ====================
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  // --- UPDATE (v7.0)
  const aiChipBg = useColorModeValue('purple.50', 'rgba(128,90,213,0.18)');
  // --- END UPDATE
  const sidebarBg = useColorModeValue('white', 'gray.800');
  const sidebarBorder = useColorModeValue('gray.200', 'gray.700');
  const headerText = useColorModeValue('gray.800', 'white');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const controlBarBg = useColorModeValue('rgba(255, 255, 255, 0.85)', 'rgba(26, 32, 44, 0.85)');

  // ==================== DIGITAL TWIN HOOK ====================
  const {
    initialize, populateScene, setGroupVisibility, scene, origin,
    selectedAsset, isReady, resetCamera, clearSelection, map, updateRaycastCache, updateLiveReadings,
    sceneProgress, // --- UPDATE (twin v8)
  } = useDigitalTwin(containerRef);

  const pendingSensorUpdatesRef = useRef({});
  const rafRef = useRef(null);
  const wsHandlerRef = useRef(null);
  const mountedRef = useRef(true);
  const predictedFailuresGroupRef = useRef(null);
  // --- UPDATE: in-flight guard prevents overlapping fetchAllData runs ---
  const fetchInFlightRef = useRef(false);
  // --- UPDATE: refs read by long-lived handlers (no re-subscription churn)
  const simulationModeRef = useRef(null);
  const sensorPipelineLinksRef = useRef({});
  const updateLiveReadingsRef = useRef(null);
  const appliedStatusRef = useRef(new Map());
  // --- END UPDATE

  useEffect(() => {
    mountedRef.current = true;
    const handleAssetClick = (asset, options) => {
      if (asset && options?.openFullModal) {
        setFullDetailAsset(asset);
      }
      if (asset && options?.openTwinDrawer) {
        setTwinOverlayAsset(asset);
        onTwinDrawerOpen();
      }
    };

    const cleanup = initialize(handleAssetClick, { openOnClick: false, flyToOnSelect: true });

    return () => {
      mountedRef.current = false;
      cleanup?.();
    };
  }, [initialize, onTwinDrawerOpen]);

  useEffect(() => {
    if (!map || !containerRef.current) return;
    let animationFrameId;

    const resizeObserver = new ResizeObserver(() => {
      if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(() => {
        if (map) map.resize();
      });
    });

    resizeObserver.observe(containerRef.current);
    return () => {
      resizeObserver.disconnect();
      if (animationFrameId) window.cancelAnimationFrame(animationFrameId);
    };
  }, [map]);

  const ensurePredictedGroup = useCallback(() => {
    if (!scene) return null;
    if (!predictedFailuresGroupRef.current) {
      let grp = scene.getObjectByName('predictedFailures');
      if (!grp) {
        grp = new THREE.Group();
        grp.name = 'predictedFailures';
        grp.renderOrder = 1000;
        scene.add(grp);
      }
      predictedFailuresGroupRef.current = grp;
    }
    return predictedFailuresGroupRef.current;
  }, [scene]);

  // --- UPDATE: simulated impact is applied through materials + the impact layer;
  // the scene is no longer rebuilt when a scenario starts or stops.
  const displayAssets = assets;
  // --- END UPDATE

  // ============================================================================
  // --- SENSOR LINK PROPAGATION ENGINE ---
  // Calculates real-time health of pipelines and assets based on their sensors
  // ============================================================================
  const entityLiveStatus = useMemo(() => {
    const map = {};
    sensors.forEach(sensor => {
      const sId = sensor.device_id || sensor.id;
      const reading = sensorReadings[sId];
      if (reading) {
        // Collect all entities this sensor is connected to
        const entityIds = [];
        if (sensor.linked_entity_id) entityIds.push(String(sensor.linked_entity_id));
        if (sensorPipelineLinks[sId]) entityIds.push(String(sensorPipelineLinks[sId]));

        if (entityIds.length > 0) {
          let status = HEALTH_STATUS.NORMAL;
          // Determine status
          if (reading.state && reading.state !== 'ok') {
            status = reading.state;
          } else if (reading.status && reading.status !== 'normal') {
            status = reading.status;
          } else {
            // Fallback logic
            const val = parseFloat(reading.value !== undefined ? reading.value : reading.psi);
            if (sensor.type === 'water_level') {
              if (reading.level === 'HIGH' || val > 85 || reading.level === 'EMPTY' || val < 10) status = HEALTH_STATUS.CRITICAL;
            } else {
              if (val > (sensor.max_threshold || 90) || val < (sensor.min_threshold || 20)) status = HEALTH_STATUS.CRITICAL;
              else if (val > (sensor.max_threshold || 90) * 0.8) status = HEALTH_STATUS.WARNING;
            }
          }

          // Propagate to linked entities (escalating severity)
          entityIds.forEach(entityId => {
            if (!map[entityId] || status === HEALTH_STATUS.CRITICAL) {
              map[entityId] = status;
            } else if (status === HEALTH_STATUS.WARNING && map[entityId] !== HEALTH_STATUS.CRITICAL) {
              map[entityId] = HEALTH_STATUS.WARNING;
            }
          });
        }
      }
    });
    return map;
  }, [sensors, sensorReadings, sensorPipelineLinks]);

  // ==================== SCENE SYNCHRONIZATION ====================
  useEffect(() => {
    if (!isReady || (displayAssets.length === 0 && sensors.length === 0)) return;

    const syncScene = async () => {
      await populateScene(displayAssets, [], sensors, assetHealthData);

      const predictionsGroup = ensurePredictedGroup();
      if (predictionsGroup) {
        while (predictionsGroup.children.length) predictionsGroup.remove(predictionsGroup.children[0]);
        // --- UPDATE: O(1) lookup with string-normalised ids (keys are strings, userData ids may be numbers)
        const assetsGroup = scene?.getObjectByName('assets');
        const objectById = new Map((assetsGroup?.children || []).map(obj => [String(obj.userData?.assetId), obj]));
        // --- END UPDATE
        for (const assetId in assetHealthData) {
          const health = assetHealthData[assetId];
          if (health?.health_status === HEALTH_STATUS.CRITICAL || health?.health_status === HEALTH_STATUS.WARNING) {
            const assetObject = objectById.get(String(assetId));
            if (assetObject) {
              const marker = new THREE.Mesh(failureIconGeometry, failureIconMaterial);
              // --- UPDATE: the twin's scene is in Mercator units (see useDigitalTwin) —
              // a 15-unit offset / 5-unit cone was planet-sized. Convert metres → units
              // and stand the cone upright (scene up = +Z).
              const mpu = typeof origin?.meterInMercatorCoordinateUnits === 'function' ? origin.meterInMercatorCoordinateUnits() : 1;
              marker.scale.setScalar(mpu);
              marker.rotation.x = -Math.PI / 2;
              marker.position.copy(assetObject.position).add(new THREE.Vector3(0, 0, 15 * mpu));
              // --- END UPDATE
              marker.userData = { type: 'predictedFailure', ...health };
              predictionsGroup.add(marker);
            }
          }
        }
      }
      // --- UPDATE: material overrides must be re-applied to freshly built meshes
      appliedStatusRef.current = new Map();
      if (typeof updateRaycastCache === 'function') updateRaycastCache();
      // --- END UPDATE
    };
    syncScene();
  }, [displayAssets, sensors, assetHealthData, isReady, populateScene, ensurePredictedGroup, scene, updateRaycastCache, origin]);

  // ============================================================================
  // --- 3D MATERIAL OVERRIDES (Forces meshes to match sensor status) ---
  // ============================================================================
  useEffect(() => {
    if (!scene || !isReady) return;
    const assetsGroup = scene.getObjectByName('assets');
    const pipelinesGroup = scene.getObjectByName('pipelines');

    // --- UPDATE: simulation states take precedence while a run is active
    const simStates = simulation.stateById;
    const applied = appliedStatusRef.current;
    // --- END UPDATE
    const applyGhostingAndColors = (group) => {
      if (!group) return;
      group.children.forEach(obj => {
        const id = String(obj.userData.assetId || obj.userData.pipelineId);

        // Combine Backend AI Health with Live Telemetry Health
        const aiHealth = assetHealthData[id];
        const liveStatus = entityLiveStatus[id];

        // --- UPDATE: simulated status overrides live/AI while a simulation is active
        const simStatus = obj.userData.assetId != null ? simStates.get(id) : undefined;
        const isCritical = simStatus ? simStatus === HEALTH_STATUS.CRITICAL : (aiHealth?.health_status === HEALTH_STATUS.CRITICAL || liveStatus === HEALTH_STATUS.CRITICAL);
        const isWarning = simStatus ? simStatus === HEALTH_STATUS.WARNING : (aiHealth?.health_status === HEALTH_STATUS.WARNING || liveStatus === HEALTH_STATUS.WARNING);
        // --- END UPDATE

        const finalStatus = isCritical ? HEALTH_STATUS.CRITICAL : isWarning ? HEALTH_STATUS.WARNING : HEALTH_STATUS.NORMAL;

        // Flag for the hook's live link propagation: simulated objects keep their glow
        obj.userData.simulationState = simStatus || null;

        // --- UPDATE: skip objects whose rendered status hasn't changed
        const key = `${obj.uuid}`;
        const signature = `${finalStatus}|${isAiMode ? 1 : 0}|${simStatus ? 's' : ''}`;
        if (applied.get(key) === signature) return;
        applied.set(key, signature);
        // --- END UPDATE

        obj.traverse(child => {
          if (child.isMesh && child.material) {
            const processMat = (mat) => {
              // Cache the original base materials so we can revert
              if (mat.userData.baseOpacity === undefined) {
                mat.userData.baseOpacity = mat.opacity;
                mat.userData.baseEmissiveIntensity = mat.emissiveIntensity;
                if (mat.emissive) mat.userData.baseEmissiveHex = mat.emissive.getHex();
              }

              if (finalStatus === HEALTH_STATUS.CRITICAL) {
                mat.opacity = 1.0;
                mat.transparent = false;
                if (!mat.emissive) mat.emissive = new THREE.Color(0x000000);
                mat.emissive.setHex(simStatus ? 0xff2d55 : 0xff0000); // Glow Red (magenta-red when simulated)
                mat.emissiveIntensity = 0.8;
              } else if (finalStatus === HEALTH_STATUS.WARNING) {
                mat.opacity = 1.0;
                mat.transparent = false;
                if (!mat.emissive) mat.emissive = new THREE.Color(0x000000);
                mat.emissive.setHex(0xffa500); // Glow Orange
                mat.emissiveIntensity = 0.8;
              } else {
                // Revert to original or AI Ghost Mode
                mat.opacity = isAiMode ? 0.15 : mat.userData.baseOpacity;
                mat.transparent = mat.opacity < 1.0;
                mat.emissiveIntensity = isAiMode ? 0.0 : mat.userData.baseEmissiveIntensity;
                if (mat.emissive && mat.userData.baseEmissiveHex !== undefined) {
                  mat.emissive.setHex(mat.userData.baseEmissiveHex);
                }
              }
              mat.needsUpdate = true;
            };
            Array.isArray(child.material) ? child.material.forEach(processMat) : processMat(child.material);
          }
        });
      });
    };

    applyGhostingAndColors(assetsGroup);
    applyGhostingAndColors(pipelinesGroup);
    // --- UPDATE
    map?.triggerRepaint?.();
  }, [isAiMode, assetHealthData, entityLiveStatus, scene, isReady, simulation.stateById, map, sceneVersion]); // --- UPDATE (v7.0): + sceneVersion (rebuilt pipes)
  // --- END UPDATE

  // ============================================================================
  // --- UPDATE: BULK HEALTH FETCH HELPER ---
  // Replaces the per-asset / getAllAssetHealth path with a batched POST to
  // /predictive-failure/health-bulk. Chunks large asset sets, retries transient
  // failures, and gracefully falls back to the legacy provider on hard failure.
  // ============================================================================
  const fetchBulkAssetHealth = useCallback(async (assetIds, token) => {
    const ids = Array.isArray(assetIds) ? assetIds.filter(Boolean) : [];
    if (ids.length === 0) return {};

    const merged = {};
    const chunks = chunkArray(ids, FETCH_CONFIG.HEALTH_BULK_CHUNK_SIZE);

    // Issue all chunk requests in parallel; tolerate partial failures.
    const results = await Promise.allSettled(chunks.map((chunk) =>
      fetchWithResilience(`${API_BASE_URL}/api/v1/predictive-failure/health-bulk`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ assetIds: chunk }),
      }).then(async (res) => {
        if (!res.ok) throw new Error(`health-bulk responded ${res.status}`);
        const json = await res.json();
        // Resilient response-shape handling: support { data: {...} } or raw map.
        return json?.data || json?.health || json || {};
      })
    ));

    let anySuccess = false;
    results.forEach((r) => {
      if (r.status === 'fulfilled' && r.value && typeof r.value === 'object') {
        anySuccess = true;
        Object.assign(merged, r.value);
      }
    });

    // Hard fallback: if every chunk failed, try the legacy aggregate provider.
    if (!anySuccess) {
      try {
        const legacy = await PredictiveFailureService.getAllAssetHealth();
        return legacy || {};
      } catch {
        return {};
      }
    }
    return merged;
  }, []);

  // ============================================================================
  // --- UPDATE: SENSOR PIPELINE LINKS HELPER (hoisted out of fetchAllData) ---
  // Promoted to a stable useCallback so both fetchAllData and future callers can
  // reuse it without redefining the closure on every refresh.
  // ============================================================================
  const fetchSensorPipelineLinks = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await fetchWithResilience(`${API_BASE_URL}/api/v1/sensors/pipeline-links`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) return {};
      const data = await response.json();
      const rawLinks = data.data || data.links || data || [];
      const formattedLinks = {};
      if (Array.isArray(rawLinks)) {
        rawLinks.forEach(link => {
          if (link.sensor_id && link.pipeline_id) {
            formattedLinks[String(link.sensor_id).trim()] = link.pipeline_id;
          }
        });
      } else if (typeof rawLinks === 'object') {
        Object.entries(rawLinks).forEach(([key, val]) => { formattedLinks[String(key).trim()] = val; });
      }
      return formattedLinks;
    } catch (error) {
      return {};
    }
  }, []);

  // ============================================================================
  // --- UPDATE: PRESSURE READING HYDRATION HELPER ---
  // ============================================================================
  const fetchInitialPressureReadings = useCallback(async (loadedSensors, token) => {
    const initialReadings = {};
    const pSensors = loadedSensors.filter(s => !s.type || s.type === 'pressure');
    if (pSensors.length === 0) return initialReadings;
    try {
      const res = await fetchWithResilience(
        `${API_BASE_URL}/api/v1/readings?limit=${FETCH_CONFIG.READINGS_LIMIT}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const json = await res.json();
        if (json.data && Array.isArray(json.data)) {
          json.data.reverse().forEach(r => {
            const dId = r.device_id || r.deviceId;
            if (dId) initialReadings[dId] = { psi: r.psi, value: r.psi, timestamp: r.timestamp || r.received_at, status: r.status || 'normal' };
          });
        }
      }
    } catch (err) { /* tolerated: telemetry hydration is best-effort */ }
    return initialReadings;
  }, []);

  // ============================================================================
  // --- UPDATE: WATER-LEVEL READING HYDRATION HELPER ---
  // Parallelized across water sensors (was previously sequential awaits).
  // ============================================================================
  const fetchInitialWaterLevels = useCallback(async (loadedSensors, token) => {
    const initialReadings = {};
    const wSensors = loadedSensors.filter(s => s.type === 'water_level');
    if (wSensors.length === 0) return initialReadings;

    await Promise.allSettled(wSensors.map(async (s) => {
      try {
        const res = await fetchWithResilience(
          `${API_BASE_URL}/api/v1/water-level/${s.device_id}?limit=1`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          const json = await res.json();
          if (json.data && json.data.length > 0) {
            const latest = json.data[0];
            initialReadings[s.device_id] = { level: latest.level, value: latest.level, timestamp: latest.timestamp };
          }
        }
      } catch (err) { /* tolerated per-sensor */ }
    }));
    return initialReadings;
  }, []);

  // ============================================================================
  // --- UPDATED: DATA FETCHING (now uses bulk health + resilient helpers) ---
  // ============================================================================
  const fetchAllData = useCallback(async (options = {}) => {
    // --- UPDATE: `manual` (button) shows the success toast; background loads stay quiet
    const manual = options === true || options?.manual === true;
    // --- END UPDATE
    // --- UPDATE: in-flight guard to prevent overlapping refreshes ---
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;

    setIsRefreshing(true);
    setFetchError(null);
    try {
      const [
        assetsResp, pipelinesResp, sensorsResp, conditionsResp,
        issuesResp, alertsResp, linksResp,
      ] = await Promise.all([
        AssetService.fetchAssets(),
        PipelineService.fetchPipelines(),
        SensorService.fetchSensors(),
        PsiConditionService.getAllConditions(),
        IssueService.fetchAllIssues(),
        ActiveAlertService.fetchActiveAlerts(),
        fetchSensorPipelineLinks(),
      ]);

      if (!mountedRef.current) return;

      const loadedSensors = sensorsResp || [];
      const loadedAssets = assetsResp || [];

      setAssets(loadedAssets);
      setPipelines(pipelinesResp || []);
      setSensors(loadedSensors);
      setSensorPipelineLinks(linksResp || {});
      setPsiConditions(conditionsResp || []);
      setIssues((issuesResp && issuesResp.data) || []);
      setAlerts((alertsResp && alertsResp.data) || []);
      // --- UPDATE (twin v8): the map is usable as soon as the network data is in — ML health and
      // telemetry hydrate behind it (the full-screen loader used to wait for everything)
      setIsLoaded(true);
      setLoadStages((s) => ({ ...s, data: 'done', health: 'active' }));
      setLoadTimes((t) => (t.data ? t : { ...t, data: Date.now() - loadT0Ref.current }));
      // --- END UPDATE

      const token = getAuthToken();

      // --- UPDATE: BULK HEALTH + telemetry hydration in parallel (were sequential) ---
      const assetIds = loadedAssets.map(a => a.id);
      const [healthData, pressureReadings, waterReadings] = await Promise.all([
        fetchBulkAssetHealth(assetIds, token),
        fetchInitialPressureReadings(loadedSensors, token),
        fetchInitialWaterLevels(loadedSensors, token),
      ]);
      if (mountedRef.current) {
        setAssetHealthData(healthData || {});
        primeAssetHealthCache(healthData || {});
        // --- UPDATE (twin v8)
        setLoadStages((s) => ({ ...s, health: 'done' }));
        setLoadTimes((t) => (t.health ? t : { ...t, health: Date.now() - loadT0Ref.current }));
        // --- END UPDATE
      }
      // --- END UPDATE
      const initialReadings = { ...pressureReadings, ...waterReadings };

      if (!mountedRef.current) return;

      setSensorReadings(initialReadings);
      if (typeof updateLiveReadings === 'function') {
        updateLiveReadings(initialReadings, linksResp);
      }

      setLastUpdateTime(new Date());
      // --- UPDATE
      if (manual) toast({ title: 'Data Refreshed', status: 'success', duration: 2000, isClosable: true, position: 'bottom-right' });
      // --- END UPDATE
    } catch (err) {
      if (!mountedRef.current) return;
      setFetchError(err?.message || 'Unknown sync error');
      setLoadStages((s) => ({ data: s.data === 'done' ? 'done' : 'error', health: s.health === 'done' ? 'done' : 'error' })); // --- UPDATE (twin v8)
      toast({ title: 'Sync Error', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      fetchInFlightRef.current = false;
      if (mountedRef.current) { setIsLoaded(true); setIsRefreshing(false); }
    }
  }, [toast, updateLiveReadings, fetchSensorPipelineLinks, fetchBulkAssetHealth, fetchInitialPressureReadings, fetchInitialWaterLevels]);

  useEffect(() => {
    if (!isReady) return;
    let isCancelled = false;
    const loadData = async () => { if (!isCancelled) await fetchAllData(); };
    loadData();
    return () => { isCancelled = true; };
  }, [isReady, fetchAllData]);

  // ============================================================================
  // --- WEBSOCKET HANDLERS ---
  // ============================================================================
  // --- UPDATE: refs kept current for the long-lived socket handlers
  simulationModeRef.current = simulationMode;
  sensorPipelineLinksRef.current = sensorPipelineLinks;
  updateLiveReadingsRef.current = updateLiveReadings;
  const latestReadingsRef = useRef({});
  latestReadingsRef.current = sensorReadings;

  // Stable flush: merges the batch once, then pushes to the scene OUTSIDE the
  // state updater (updaters must be pure — StrictMode runs them twice).
  const flushPendingSensorUpdates = useCallback(() => {
    rafRef.current = null;
    const updates = pendingSensorUpdatesRef.current;
    if (updates && Object.keys(updates).length > 0) {
      pendingSensorUpdatesRef.current = {};
      const newState = { ...latestReadingsRef.current, ...updates };
      latestReadingsRef.current = newState;
      setSensorReadings(newState);
      // --- Pass the links map during live WebSocket updates ---
      updateLiveReadingsRef.current?.(newState, sensorPipelineLinksRef.current);
    }
  }, []);
  // --- END UPDATE

  useEffect(() => {
    const handleSensorUpdate = (message) => {
      // --- UPDATE: read simulation state through a ref (no re-subscribe on change)
      if (!message || simulationModeRef.current) return;
      // --- END UPDATE

      try {
        const data = message.payload || message.data || message;
        const deviceId = data.device_id || data.deviceId || data.device;

        if (!deviceId) return;

        const pressureValue = data.psi !== undefined ? data.psi : (data.pressure !== undefined ? data.pressure : data.value);
        let newReading = null;

        if (pressureValue !== undefined) {
          newReading = {
            psi: pressureValue,
            value: pressureValue,
            timestamp: data.timestamp || data.received_at || new Date().toISOString(),
            status: data.status || 'normal',
            state: data.state // From DigitalTwinEngine
          };
        } else if (data.level !== undefined) {
          newReading = {
            level: data.level,
            value: data.level,
            timestamp: data.timestamp || data.received_at || new Date().toISOString(),
            state: data.state
          };
        }

        if (newReading) {
          pendingSensorUpdatesRef.current[deviceId] = newReading;
          if (!rafRef.current) rafRef.current = requestAnimationFrame(flushPendingSensorUpdates);
        }
      } catch (err) {
        console.warn("[DigitalTwin] WS Parse Error", err);
      }
    };

    wsHandlerRef.current = handleSensorUpdate;

    if (isLive) {
      try {
        webSocketService.connect();
        webSocketService.subscribe('new_pressure_reading', handleSensorUpdate);
        webSocketService.subscribe('new_water_level_reading', handleSensorUpdate);
        webSocketService.subscribe('psi_update', handleSensorUpdate);
        webSocketService.subscribe('level_update', handleSensorUpdate);
        webSocketService.subscribe('telemetry', handleSensorUpdate);
      } catch (err) { /* connection errors handled by service layer */ }
    }

    return () => {
      try {
        if (wsHandlerRef.current) {
          webSocketService.unsubscribe?.('new_pressure_reading', wsHandlerRef.current);
          webSocketService.unsubscribe?.('new_water_level_reading', wsHandlerRef.current);
          webSocketService.unsubscribe?.('psi_update', wsHandlerRef.current);
          webSocketService.unsubscribe?.('level_update', wsHandlerRef.current);
          webSocketService.unsubscribe?.('telemetry', wsHandlerRef.current);
        }
      } catch (err) { /* ignore unsubscribe errors during teardown */ }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      pendingSensorUpdatesRef.current = {};
    };
  // --- UPDATE: subscribe/unsubscribe only when live mode toggles
  }, [isLive, flushPendingSensorUpdates]);
  // --- END UPDATE

  const [currentTimestamp, setCurrentTimestamp] = useState(null);

  const handleTimeChange = useCallback(async (timestamp) => {
    if (isLive) setIsLive(false);
    setCurrentTimestamp(timestamp);
    // --- UPDATE (v7.0): only the latest request is applied (responses can arrive out of order)
    const reqId = ++historicalReqRef.current;
    setHistoricalLoading(true);
    // --- END UPDATE
    try {
      const token = getAuthToken();
      // Fetch global map sensors
      const res = await fetchWithResilience(
        `${API_BASE_URL}/api/v1/sensors/historical?timestamp=${timestamp.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const historicalReadings = await res.json();
        if (reqId !== historicalReqRef.current || !mountedRef.current) return; // --- UPDATE (v7.0): stale response
        setSensorReadings(historicalReadings || {});
        if (typeof updateLiveReadings === 'function') updateLiveReadings(historicalReadings || {}, sensorPipelineLinks);
      }
    } catch (error) { /* historical fetch is non-fatal */ }
    // --- UPDATE (v7.0)
    finally { if (reqId === historicalReqRef.current && mountedRef.current) setHistoricalLoading(false); }
    // --- END UPDATE
  }, [isLive, updateLiveReadings, sensorPipelineLinks]);

  const handleGoLive = useCallback(() => {
    historicalReqRef.current += 1; // --- UPDATE (v7.0): drop any pending historical response
    setHistoricalLoading(false);
    if (!isLive) {
      setSensorReadings({});
      setIsLive(true);
      setCurrentTimestamp(null);
      fetchAllData();
    }
  }, [isLive, fetchAllData]);

  // --- UPDATE (v7.0): honours the requested value and never reads stale state — presets and
  // Show/Hide All call this for several layers in one tick (was: always toggled the old value).
  // The scene groups follow the state in the effect below.
  const handleLayerToggle = useCallback((layerKey, explicit) => {
    setLayerVisibility(prev => {
      const next = typeof explicit === 'boolean' ? explicit : !prev[layerKey];
      if (prev[layerKey] === next) return prev;
      return { ...prev, [layerKey]: next };
    });
  }, []);
  useEffect(() => {
    if (!setGroupVisibility || !isReady) return;
    Object.entries(layerVisibility).forEach(([k, v]) => { try { setGroupVisibility(k, v); } catch (_) { /* group not built yet */ } });
    map?.triggerRepaint?.();
  }, [layerVisibility, setGroupVisibility, isReady, sceneVersion, map]);
  // --- END UPDATE

  // --- UPDATE (v7.0): FlowSense twin AI/ML (declared before the export / handlers that use it)
  const twinIntel = useTwinIntel({
    assets, pipelines, sensors, links: sensorPipelineLinks, readings: sensorReadings, health: assetHealthData,
    alerts, issues, isLive, simStates: simulation.stateById, floodPredictions, serverAnomalies,
  });
  // --- END UPDATE

  const handleExportData = useCallback(() => {
    const exportData = {
      timestamp: new Date().toISOString(), assets: assets.length, pipelines: pipelines.length, sensors: sensors.length, issues: issues.length,
      alerts: alerts.length, telemetry: Object.keys(sensorReadings).length,
      // --- UPDATE (v7.0): the AI picture with the counts
      mode: isLive ? 'live' : `historical ${currentTimestamp ? new Date(currentTimestamp).toISOString() : ''}`,
      flowsense_ai: twinIntel ? {
        summary: twinIntel.summary,
        findings: twinIntel.findings.map((f) => ({ level: f.level, title: f.title, detail: f.detail })),
        risks: twinIntel.risks.slice(0, 100).map((r) => ({ asset_id: r.id, name: r.name, score: r.score, band: r.band, drivers: r.drivers, actions: r.actions })),
        suspected_leaks: twinIntel.leaks.map((l) => ({ pipeline_id: l.pipelineId, confidence: l.confidence, sensors: l.sensors, text: l.text })),
      } : null,
      layers: layerVisibility,
      // --- END UPDATE
    };
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(exportData, null, 2));
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', `flowSense-export-${Date.now()}.json`);
    linkElement.click();
  }, [assets, pipelines, sensors, issues, alerts, sensorReadings, twinIntel, isLive, currentTimestamp, layerVisibility]); // --- UPDATE (v7.0)

  // ==================== COMPUTED VALUES ====================
  const layerCounts = useMemo(() => ({
    terrain: assets.reduce((n, a) => (a.ground_elevation_m != null ? n + 1 : n), 0), // --- UPDATE (twin v8)
    assets: assets.length,
    pipelines: pipelines.length,
    sensors: sensors.length,
    issues: issues.length,
    alerts: alerts.length,
    // --- UPDATE (v7.0): ML warning + critical assets (the cones)
    predictedFailures: Object.values(assetHealthData || {}).filter((h) => h?.health_status === HEALTH_STATUS.CRITICAL || h?.health_status === HEALTH_STATUS.WARNING).length,
    // --- END UPDATE
  }), [assets, pipelines.length, sensors.length, issues.length, alerts.length, assetHealthData]);

  const criticalAssets = useMemo(() => assets.filter(asset => assetHealthData[asset.id]?.health_status === HEALTH_STATUS.CRITICAL).length, [assets, assetHealthData]);

  // ============================================================================
  // --- UPDATE: AI + SIMULATION DERIVED DATA
  // ============================================================================
  const aiRisk = useMemo(() => {
    let critical = 0, warning = 0, scored = 0;
    for (const id in assetHealthData) {
      const st = assetHealthData[id]?.health_status;
      if (!st) continue;
      scored += 1;
      if (st === HEALTH_STATUS.CRITICAL) critical += 1;
      else if (st === HEALTH_STATUS.WARNING) warning += 1;
    }
    return { critical, warning, scored };
  }, [assetHealthData]);

  // Median, not mean: a few mis-located assets (e.g. coordinates in Texas) dragged the
  // averaged centre off the Bahamas, so the storm outlook was fetched for the wrong place.
  const networkCentroid = useMemo(() => {
    const lats = [], lngs = [];
    for (const a of assets) {
      const la = Number(a.latitude), lo = Number(a.longitude);
      if (Number.isFinite(la) && Number.isFinite(lo) && !(la === 0 && lo === 0) && Math.abs(la) <= 90 && Math.abs(lo) <= 180) { lats.push(la); lngs.push(lo); }
    }
    if (!lats.length) return null;
    lats.sort((x, y) => x - y); lngs.sort((x, y) => x - y);
    const mid = Math.floor(lats.length / 2);
    return { lat: lats[mid], lng: lngs[mid] };
  }, [assets]);

  const assetsByIdMap = useMemo(() => new Map(assets.map(a => [String(a.id), a])), [assets]);

  // Downstream assets reached through each origin in the cascade
  const simDownstreamCounts = useMemo(() => {
    const m = new Map();
    for (const r of simulation.result?.affectedNodes || []) {
      if (r.cascade_from_asset_id != null && String(r.cascade_from_asset_id) !== String(r.asset_id)) {
        const k = String(r.cascade_from_asset_id);
        m.set(k, (m.get(k) || 0) + 1);
      }
    }
    return m;
  }, [simulation.result]);

  const simulationOverlay = useMemo(() => (simulation.meta && simulation.result
    ? { title: simulation.meta.title, scenario: simulation.meta.scenario, summary: simulation.result.summary }
    : null), [simulation.meta, simulation.result]);

  /** Fly to an asset from the simulation results and open its details. */
  const handleFocusAsset = useCallback((record) => {
    const asset = assetsByIdMap.get(String(record.asset_id));
    const lat = Number(record.latitude ?? asset?.latitude);
    const lng = Number(record.longitude ?? asset?.longitude);
    if (map && Number.isFinite(lat) && Number.isFinite(lng)) {
      try { map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom?.() ?? 16, 17), pitch: 60, essential: true }); } catch (_) { /* noop */ }
    }
    if (asset) setFullDetailAsset(asset);
  }, [assetsByIdMap, map]);

  /** From the AI outlook chip: open Tools and run the live flood forecast. */
  const handleRunForecast = useCallback(() => {
    const sc = SCENARIO_BY_ID.flood_forecast;
    setIsSidebarOpen(true);
    setSidebarTab(2);
    simulation.run(sc, defaultValuesFor(sc), { assetId: selectedAsset?.id ?? null, selectedAsset, centroid: networkCentroid })
      .then((body) => { if (body) setSimulationMode(sc.title); });
  }, [simulation, selectedAsset, networkCentroid]);
  // --- END UPDATE

  // ============================================================================
  // --- UPDATE (v7.0): FLOWSENSE TWIN AI / ML
  // ============================================================================
  const sensorsByKey = useMemo(() => new Map(sensors.map((s) => [twinSensorKey(s), s])), [sensors]);
  const pipelinesById = useMemo(() => new Map(pipelines.map((p) => [String(p.id), p])), [pipelines]);
  const predictedFailureList = useMemo(() => Object.entries(assetHealthData || {})
    .filter(([, h]) => h?.health_status === HEALTH_STATUS.CRITICAL || h?.health_status === HEALTH_STATUS.WARNING)
    .map(([id, h]) => ({ asset_id: id, ...h })), [assetHealthData]);
  const timelineEvents = useMemo(() => (alerts || []).map((a) => ({
    timestamp: a.created_at || a.triggered_at || a.timestamp || a.createdAt, level: a.severity, label: a.message || a.alert_message || a.detail,
  })), [alerts]);

  const flyTo = useCallback((c, zoom = 17) => {
    const lat = Number(c?.lat ?? c?.latitude), lng = Number(c?.lng ?? c?.longitude);
    if (!map || !Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    try { map.flyTo({ center: [lng, lat], zoom: Math.max(map.getZoom?.() ?? 16, zoom), pitch: 60, essential: true }); return true; } catch (_) { return false; }
  }, [map]);

  const openSensor = useCallback((sensorOrKey) => {
    const key = typeof sensorOrKey === 'object' && sensorOrKey ? twinSensorKey(sensorOrKey) : String(sensorOrKey ?? '');
    if (!key) return;
    setDrawerSensorKey(key);
  }, []);
  const drawerSensor = drawerSensorKey ? (sensorsByKey.get(drawerSensorKey) || (selectedAsset && twinSensorKey(selectedAsset) === drawerSensorKey ? selectedAsset : null)) : null;
  const drawerLinkedEntity = useMemo(() => {
    if (!drawerSensor) return null;
    const pid = sensorPipelineLinks[drawerSensorKey];
    if (pid !== undefined && pid !== null && pipelinesById.get(String(pid))) return { ...pipelinesById.get(String(pid)), isPipeline: true };
    const lid = drawerSensor.linked_entity_id;
    if (lid === undefined || lid === null) return null;
    if (drawerSensor.linked_entity_type === 'pipeline' && pipelinesById.get(String(lid))) return { ...pipelinesById.get(String(lid)), isPipeline: true };
    return assetsByIdMap.get(String(lid)) || null;
  }, [drawerSensor, drawerSensorKey, sensorPipelineLinks, pipelinesById, assetsByIdMap]);

  /** History for the sensor drawer (pre-trains the sensor's ML model when it is new). */
  const fetchSensorHistory = useCallback(async (sensor) => {
    const key = twinSensorKey(sensor);
    if (!key) return;
    const token = getAuthToken();
    const url = sensor.type === 'water_level'
      ? `${API_BASE_URL}/api/v1/water-level/${encodeURIComponent(key)}?limit=120`
      : `${API_BASE_URL}/api/v1/sensors/${encodeURIComponent(key)}/readings?limit=120`;
    try {
      const res = await fetchWithResilience(url, { headers: { Authorization: `Bearer ${token}` } }, { retries: 0 });
      if (!res.ok) return;
      const json = await res.json();
      const rows = Array.isArray(json?.data) ? json.data : Array.isArray(json?.data?.data) ? json.data.data : Array.isArray(json) ? json : [];
      if (rows.length) twinIntel.seed(key, rows);
    } catch (_) { /* history is optional */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twinIntel.seed]);

  const handleAiAction = useCallback((a) => {
    if (!a) return;
    if (a.type === 'sensor') {
      const s = sensorsByKey.get(String(a.id));
      if (s) flyTo(twinCoords(s) || a);
      openSensor(a.id);
      return;
    }
    if (a.type === 'pipeline') {
      const p = pipelinesById.get(String(a.id));
      const path = normalizePath(p?.path_coordinates);
      const mid = path.length ? path[Math.floor(path.length / 2)] : null;
      flyTo(mid || a, 16);
      if (a.sensor) openSensor(a.sensor);
      return;
    }
    if (a.type === 'asset') handleFocusAsset({ asset_id: a.id, latitude: a.lat, longitude: a.lng });
  }, [sensorsByKey, pipelinesById, flyTo, openSensor, handleFocusAsset]);

  const toggleAiMode = useCallback(() => setIsAiMode((v) => !v), []);
  const toggleFocusMode = useCallback(() => {
    setIsFocusMode((on) => {
      if (!on) { preFocusSidebarRef.current = isSidebarOpen; setIsSidebarOpen(false); } else setIsSidebarOpen(preFocusSidebarRef.current);
      return !on;
    });
  }, [isSidebarOpen]);
  useEffect(() => {
    if (!isFocusMode) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') toggleFocusMode(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFocusMode, toggleFocusMode]);

  const handlePipelinesBuilt = useCallback(() => {
    if (typeof updateRaycastCache === 'function') updateRaycastCache();
    setSceneVersion((v) => v + 1);
  }, [updateRaycastCache]);

  // ----- targeted refreshes for operational events (no scene rebuild for an alert) -----
  const refreshOperational = useCallback(async (kinds) => {
    const want = new Set(kinds);
    const token = getAuthToken();
    const jobs = [];
    if (want.has('alerts')) jobs.push(ActiveAlertService.fetchActiveAlerts().then((r) => { if (mountedRef.current) setAlerts((r && r.data) || []); }).catch(() => {}));
    if (want.has('issues')) jobs.push(IssueService.fetchAllIssues().then((r) => { if (mountedRef.current) setIssues((r && r.data) || []); }).catch(() => {}));
    if (want.has('health')) jobs.push(fetchBulkAssetHealth(assetsRef.current.map((a) => a.id), token).then((h) => { if (mountedRef.current && h && Object.keys(h).length) { setAssetHealthData(h); primeAssetHealthCache(h); } }).catch(() => {}));
    if (want.has('telemetry')) {
      jobs.push(Promise.all([fetchInitialPressureReadings(sensorsRef.current, token), fetchInitialWaterLevels(sensorsRef.current, token)]).then(([pr, wr]) => {
        if (!mountedRef.current || !isLiveRef.current) return;
        const merged = { ...latestReadingsRef.current, ...pr, ...wr };
        latestReadingsRef.current = merged;
        setSensorReadings(merged);
        updateLiveReadingsRef.current?.(merged, sensorPipelineLinksRef.current);
      }).catch(() => {}));
    }
    await Promise.all(jobs);
    if (mountedRef.current) setLastUpdateTime(new Date());
  }, [fetchBulkAssetHealth, fetchInitialPressureReadings, fetchInitialWaterLevels]);
  const assetsRef = useRef([]); assetsRef.current = assets;
  const sensorsRef = useRef([]); sensorsRef.current = sensors;
  const isLiveRef = useRef(true); isLiveRef.current = isLive;
  const refreshOperationalRef = useRef(refreshOperational); refreshOperationalRef.current = refreshOperational;
  const fetchAllDataRef = useRef(null); fetchAllDataRef.current = fetchAllData;
  const queueRefresh = useCallback((kind) => {
    opQueueRef.current.add(kind);
    if (opTimerRef.current) return;
    opTimerRef.current = setTimeout(() => {
      opTimerRef.current = null;
      const kinds = [...opQueueRef.current]; opQueueRef.current.clear();
      refreshOperationalRef.current(kinds);
    }, 1200);
  }, []);

  useEffect(() => {
    if (!isLive) return undefined;
    const handlers = {};
    const on = (evt, fn) => { handlers[evt] = fn; try { webSocketService.subscribe(evt, fn); } catch (_) { /* service layer handles */ } };
    TWIN_EVENTS.alerts.forEach((e) => on(e, () => queueRefresh('alerts')));
    TWIN_EVENTS.issues.forEach((e) => on(e, () => queueRefresh('issues')));
    TWIN_EVENTS.health.forEach((e) => on(e, () => queueRefresh('health')));
    TWIN_EVENTS.scene.forEach((e) => on(e, () => {
      if (sceneTimerRef.current) return;
      sceneTimerRef.current = setTimeout(() => { sceneTimerRef.current = null; fetchAllDataRef.current?.(); }, 2000);
    }));
    on('asset_status_update', (m) => {
      const p = payloadOf(m);
      const id = p.assetId ?? p.asset_id ?? p.id;
      if (id === undefined || id === null || !p.status) return;
      statusQueueRef.current.set(String(id), p.status);
      if (statusTimerRef.current) return;
      statusTimerRef.current = setTimeout(() => {
        statusTimerRef.current = null;
        const q = new Map(statusQueueRef.current); statusQueueRef.current.clear();
        setAssets((prev) => { let changed = false; const next = prev.map((a) => { const st = q.get(String(a.id)); if (st && st !== a.status) { changed = true; return { ...a, status: st }; } return a; }); return changed ? next : prev; });
      }, 1500);
    });
    on('critical_anomaly', (m) => {
      const p = payloadOf(m);
      const key = String(p.device_id ?? p.deviceId ?? p.sensor_id ?? 'unknown');
      setServerAnomalies((prev) => [{ ...p, timestamp: p.timestamp || new Date().toISOString() }, ...prev.filter((x) => String(x.device_id ?? x.deviceId ?? x.sensor_id) !== key)].slice(0, 20));
      queueRefresh('alerts');
      const last = anomalyToastRef.current.get(key) || 0;
      if (Date.now() - last > 5 * 60000) {
        anomalyToastRef.current.set(key, Date.now());
        toast({ title: 'Critical anomaly', description: `${key !== 'unknown' ? `Sensor ${key}: ` : ''}${p.message || p.detail || 'reported by the server'}`, status: 'error', duration: 6000, isClosable: true, position: 'top-right' });
      }
    });
    on('ai_flood_prediction', (m) => {
      const p = payloadOf(m);
      const id = String(p.well_id ?? p.wellId ?? '');
      if (!id) return;
      setFloodPredictions((prev) => [p, ...prev.filter((x) => String(x.well_id ?? x.wellId) !== id)].slice(0, 50));
    });
    // live-feed stats: one extra lightweight listener on the telemetry events
    const count = () => { const t = Date.now(); const arr = msgTimesRef.current; arr.push(t); while (arr.length && t - arr[0] > 60000) arr.shift(); };
    TELEMETRY_EVENTS.forEach((e) => on(e, count));
    const statsTimer = setInterval(() => {
      const t = Date.now(); const arr = msgTimesRef.current; while (arr.length && t - arr[0] > 60000) arr.shift();
      setLiveStats((prev) => {
        const next = { connected: socketConnected(), lastMessageAt: arr.length ? arr[arr.length - 1] : prev.lastMessageAt, perMinute: arr.length };
        return prev.connected === next.connected && prev.lastMessageAt === next.lastMessageAt && prev.perMinute === next.perMinute ? prev : next;
      });
    }, 5000);
    // fallback while the socket is down (tab visible only)
    const pollTimer = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      if (socketConnected() === false) refreshOperationalRef.current(['alerts', 'issues', 'telemetry']);
    }, OFFLINE_POLL_MS);
    return () => {
      Object.entries(handlers).forEach(([evt, fn]) => { try { webSocketService.unsubscribe?.(evt, fn); } catch (_) { /* teardown */ } });
      clearInterval(statsTimer); clearInterval(pollTimer);
      clearTimeout(opTimerRef.current); opTimerRef.current = null;
      clearTimeout(sceneTimerRef.current); sceneTimerRef.current = null;
      clearTimeout(statusTimerRef.current); statusTimerRef.current = null;
    };
  }, [isLive, queueRefresh, toast]);

  const arContext = useMemo(() => ({ selectedAsset, assets, pipelines, sensors, readings: sensorReadings, origin, findings: twinIntel.findings }), [selectedAsset, assets, pipelines, sensors, sensorReadings, origin, twinIntel.findings]);
  const criticalFindings = twinIntel.findings.filter((f) => f.level === 'critical').length;
  // --- END UPDATE

  // --- UPDATE (twin v8): responsive hub — full width on phones, 360 px on tablets, 420 px on wide screens
  const SIDEBAR_WIDTH_PX = useBreakpointValue({ base: typeof window !== 'undefined' ? Math.min(400, window.innerWidth - 16) : 360, md: 360, xl: 400, '2xl': 440 }, { fallback: 'xl' }) ?? 400;
  const terrainInfo = useMemo(() => terrainStats(assets), [assets]);
  const loaderStages = useMemo(() => {
    const sec = (ms) => (ms != null ? `${(ms / 1000).toFixed(1)}s` : null);
    return [
      { key: 'engine', label: 'Map & 3D engine', status: isReady ? 'done' : 'active' },
      { key: 'data', label: 'Assets, pipelines, sensors, alerts', status: isReady ? loadStages.data : 'pending', detail: loadStages.data === 'done' ? `${assets.length.toLocaleString()} assets · ${sec(loadTimes.data)}` : null },
      { key: 'health', label: 'ML health & live telemetry', status: loadStages.data === 'done' ? loadStages.health : 'pending', detail: sec(loadTimes.health) },
      { key: 'scene', label: '3D scene', status: sceneProgress?.done ? 'done' : sceneProgress?.total ? 'active' : 'pending', detail: sceneProgress?.total ? `${(sceneProgress.loaded || 0).toLocaleString()} / ${sceneProgress.total.toLocaleString()}` : null },
    ];
  }, [isReady, loadStages, loadTimes, assets.length, sceneProgress]);
  // --- END UPDATE

  // --- UPDATE: stable callback — an inline arrow re-rendered the memoised drawer every page render
  const twinAssetNameRef = useRef(null);
  twinAssetNameRef.current = twinOverlayAsset?.name;
  const handleCriticalAlarm = useCallback((incomingAlarms) => {
    toast({
      title: "CRITICAL SYSTEM ALERT",
      description: `${incomingAlarms.length} critical alarms detected on ${twinAssetNameRef.current || 'Asset'}. Action required.`,
      status: "error",
      duration: 8000,
      isClosable: true,
      position: "top-right"
    });
  }, [toast]);
  // --- END UPDATE

  // --- Isolate only the sensors linked to the selected asset for the UI Drawer ---
  const twinOverlaySensors = useMemo(() => {
    if (!twinOverlayAsset) return [];
    // --- UPDATE (twin overlay v6): sensors linked through asset_id were missed, so wells with a
    // water-level sensor opened as "no sensor"
    const id = String(twinOverlayAsset.id);
    return sensors.filter(s =>
      String(s.linked_entity_id) === id ||
      String(s.asset_id) === id ||
      String(sensorPipelineLinks[s.device_id || s.id]) === id
    );
    // --- END UPDATE
  }, [twinOverlayAsset, sensors, sensorPipelineLinks]);

  // --- UPDATE (twin overlay v6): drawer callbacks
  const closeTwinDrawer = useCallback(() => {
    onTwinDrawerClose();
    setTwinOverlayAsset(null);
  }, [onTwinDrawerClose]);
  const focusFromTwinDrawer = useCallback((assetId) => {
    if (!assetsByIdMap.has(String(assetId))) {
      toast({ status: 'info', title: 'Asset is not loaded on the twin', description: 'Zoom out or clear the filters to show it.', duration: 4000, position: 'top-right' });
      return;
    }
    closeTwinDrawer();
    handleFocusAsset({ asset_id: assetId });
  }, [assetsByIdMap, closeTwinDrawer, handleFocusAsset, toast]);
  // --- END UPDATE

  return (
    <Flex h="calc(100dvh - 70px)" minH="560px" w="100%" position="relative" bg={pageBg} overflow="hidden">{/* --- UPDATE (twin v8): dvh — mobile browser bars no longer cut the page */}

      {/* ==================== MAIN CANVAS AREA ==================== */}
      <Box position="relative" flex="1" transition="margin-right 0.3s ease-in-out" mr={isSidebarOpen ? `${SIDEBAR_WIDTH_PX}px` : "0"}>
        <div ref={containerRef} style={{ height: '100%', width: '100%', borderRadius: isSidebarOpen ? '0 16px 16px 0' : '0' }} />

        <DigitalTwinControls
          map={map}
          initialViewState={{ pitch: 65, bearing: -17.6 }}
          selectedObject={selectedAsset}
          layerVisibility={layerVisibility}
          onLayerToggle={handleLayerToggle}
          isAiMode={isAiMode}
          toggleAiMode={toggleAiMode} // --- UPDATE (v7.0): stable
          // --- UPDATE (v7.0)
          onResetView={resetCamera}
          toggleFocusMode={toggleFocusMode}
          isFocusMode={isFocusMode}
          layerCounts={layerCounts}
          // --- END UPDATE
        />

        <TimeSlider
          onTimeChange={handleTimeChange}
          onGoLive={handleGoLive}
          isLive={isLive}
          hidden={!isDataOverlayMinimized && !isFocusMode} // --- UPDATE (v7.0): available in focus mode
          // --- UPDATE (v7.0)
          events={timelineEvents}
          loading={historicalLoading}
          // --- END UPDATE
        />

        <Anomalies3DLayer scene={scene} origin={origin} map={map} visible={layerVisibility.alerts} />

        {/* --- UPDATE: simulation impact (markers, flood columns, storm track) */}
        <SimulationImpact3DLayer
          scene={scene}
          origin={origin}
          map={map}
          assets={assets}
          result={simulation.result}
          scenario={simulation.meta?.scenario}
          stateAt={simulation.stateAt}
          playbackHour={simulation.playbackHour}
          visible={showSimulationLayer}
          onLayerUpdated={updateRaycastCache}
        />
        {/* --- END UPDATE */}
        {/* --- UPDATE (twin v8): TessaDEM ground elevation layer + legend */}
        <TerrainElevation3DLayer scene={scene} origin={origin} map={map} assets={assets} visible={!!layerVisibility.terrain} />
        <TerrainLegend stats={terrainInfo} visible={!!layerVisibility.terrain} />
        {/* --- END UPDATE */}
        {/* --- UPDATE (elevation): rain + downhill overflow for the run-off scenario */}
        {simulation.meta?.scenario === 'rainfall_runoff' && (
          <RunoffFlow3DLayer
            scene={scene}
            origin={origin}
            map={map}
            assets={assets}
            result={simulation.result}
            playbackHour={simulation.playbackHour}
            visible={showSimulationLayer}
          />
        )}
        {/* --- END UPDATE */}

        {/* --- UPDATE (v7.0): always mounted (hidden via `visible`), stable callback — the inline
            arrow rebuilt every pipe on every telemetry flush; unmounting left the meshes behind */}
        <Pipeline3DLayer
          scene={scene}
          origin={origin}
          pipelines={pipelines}
          simulationMode={simulationMode}
          visible={layerVisibility.pipelines}
          highlightId={selectedAsset?.isPipeline || selectedAsset?.type === 'pipeline' ? selectedAsset?.id : null}
          onLayerUpdated={handlePipelinesBuilt}
        />
        {/* --- END UPDATE */}

        <HStack
          position="absolute" top={4} left={4}
          bg={controlBarBg} backdropFilter="blur(16px)"
          p={2} px={4} borderRadius="full" spacing={4} zIndex={10}
          borderWidth="1px" borderColor={sidebarBorder}
          boxShadow="lg"
        >
          <HStack spacing={2}>
            <Badge colorScheme={isLive ? 'green' : 'gray'} variant="solid" px={3} py={1} borderRadius="full" display="flex" alignItems="center">
              {isLive && <Box as="span" w="2" h="2" borderRadius="full" bg="white" mr={2} animation={`${pulseGlow} 2s infinite`} />}
              {isLive ? 'LIVE TELEMETRY' : 'HISTORICAL'}
            </Badge>

            {isAiMode && (
              <Badge colorScheme="purple" variant="solid" display="flex" alignItems="center" px={3} py={1} borderRadius="full">
                <Icon as={FaBrain} mr={2} /> AI PREDICTIVE
              </Badge>
            )}

            {simulationMode && (
              <Badge colorScheme="orange" variant="solid" px={3} py={1} borderRadius="full">
                SIM: {simulationMode.toUpperCase()}
              </Badge>
            )}

            {/* --- UPDATE: running indicator + AI weather outlook */}
            {simulation.isRunning && (
              <Badge colorScheme="purple" variant="subtle" px={3} py={1} borderRadius="full" display="flex" alignItems="center" gap={2}>
                <Spinner size="xs" /> SIMULATING{simulation.progress != null ? ` ${simulation.progress}%` : ''}
              </Badge>
            )}
            <AiStormOutlook center={networkCentroid} onRunForecast={handleRunForecast} />
            {/* --- END UPDATE */}

            {criticalAssets > 0 && (
              <Badge colorScheme="red" variant="solid" px={3} py={1} borderRadius="full">
                {criticalAssets} CRITICAL
              </Badge>
            )}
            {/* --- UPDATE (v7.0): FlowSense AI chip + live feed */}
            <Tooltip label={twinIntel.summary.score === null ? 'Network health appears once data has loaded' : `Network health ${twinIntel.summary.score}/100 · ${twinIntel.summary.anomalies} anomaly(ies) · ${twinIntel.summary.leaks} suspected leak(s) — open the AI tab`} hasArrow>
              <Button size="xs" borderRadius="full" bg={aiChipBg} leftIcon={<Icon as={FaBrain} color="purple.400" />} onClick={() => { setIsSidebarOpen(true); setSidebarTab(3); }} aria-label="Open FlowSense AI">
                {twinIntel.summary.score === null ? 'AI —' : `AI ${twinIntel.summary.score}`}
                {twinIntel.summary.leaks > 0 && <Badge ml={2} colorScheme="red" borderRadius="full">{twinIntel.summary.leaks} LEAK</Badge>}
                {twinIntel.summary.anomalies > 0 && <Badge ml={2} colorScheme="orange" borderRadius="full">{twinIntel.summary.anomalies} ANOM</Badge>}
              </Button>
            </Tooltip>
            <TwinLiveIndicator connected={liveStats.connected} isLive={isLive} lastMessageAt={liveStats.lastMessageAt} perMinute={liveStats.perMinute} />
            {/* --- END UPDATE */}
          </HStack>

          <Divider orientation="vertical" h="24px" borderColor="gray.400" />

          <HStack spacing={1}>
            <Tooltip label="Refresh Sync" hasArrow>
              <IconButton icon={<RepeatIcon />} size="sm" variant="ghost" colorScheme="blue" borderRadius="full" isLoading={isRefreshing} onClick={() => fetchAllData({ manual: true })} aria-label="Refresh data" />
            </Tooltip>
            <Tooltip label="Reset Camera" hasArrow>
              <IconButton icon={<ViewIcon />} size="sm" variant="ghost" colorScheme="blue" borderRadius="full" onClick={resetCamera} aria-label="Reset camera" />
            </Tooltip>
            <Menu placement="bottom-end">
              <Tooltip label="System Options" hasArrow>
                <MenuButton as={IconButton} icon={<SettingsIcon />} size="sm" variant="ghost" colorScheme="blue" borderRadius="full" />
              </Tooltip>
              <MenuList bg={sidebarBg} borderColor={sidebarBorder} shadow="xl" borderRadius="xl">
                <MenuItem icon={<DownloadIcon />} onClick={handleExportData} fontWeight="600">Export State JSON</MenuItem>
                <MenuItem icon={<ViewOffIcon />} onClick={clearSelection} isDisabled={!selectedAsset} fontWeight="600">Clear Selection</MenuItem>
                {/* --- UPDATE */}
                <MenuItem icon={showSimulationLayer ? <ViewOffIcon /> : <ViewIcon />} onClick={() => setShowSimulationLayer(v => !v)} isDisabled={!simulation.result} fontWeight="600">
                  {showSimulationLayer ? 'Hide' : 'Show'} Simulation Layer
                </MenuItem>
                {/* --- END UPDATE */}
              </MenuList>
            </Menu>
          </HStack>
        </HStack>

        {/* --- UPDATE: non-blocking error banner with retry CTA --- */}
        {fetchError && isLoaded && (
          <HStack
            position="absolute" top={20} left={4} zIndex={11}
            bg="red.500" color="white" px={4} py={2} borderRadius="lg" boxShadow="lg" spacing={3}
          >
            <Text fontWeight="700" fontSize="sm">Sync failed: {fetchError}</Text>
            <Button size="xs" colorScheme="whiteAlpha" onClick={() => fetchAllData({ manual: true })} isLoading={isRefreshing}>Retry</Button>
          </HStack>
        )}

        {/* --- UPDATE: simulation broadcast from another operator */}
        {simulation.externalRun && !simulation.result && (
          <HStack
            position="absolute" top={fetchError ? 32 : 20} left={4} zIndex={11}
            bg="purple.600" color="white" px={4} py={2} borderRadius="lg" boxShadow="lg" spacing={3}
          >
            <Icon as={FaBrain} />
            <Text fontWeight="700" fontSize="sm">
              Another operator ran a {String(simulation.externalRun.scenario).replace(/_/g, ' ')} simulation
              {simulation.externalRun.affectedCount != null ? ` (${simulation.externalRun.affectedCount} assets)` : ''}
            </Text>
            <Button size="xs" colorScheme="whiteAlpha" onClick={simulation.dismissExternal}>Dismiss</Button>
          </HStack>
        )}
        {/* --- END UPDATE */}

        {/* --- UPDATE (twin v8): staged loader — full screen until the network data is in, then a
            compact pill while the 3D scene builds (the map is usable meanwhile) */}
        <TwinLoadingOverlay stages={loaderStages} sceneProgress={sceneProgress} fullScreen={!isLoaded} />
        {/* --- END UPDATE */}
      </Box>

      {/* ==================== SIDEBAR TOGGLE ==================== */}
      <IconButton
        aria-label="Toggle Sidebar"
        display={isFocusMode ? 'none' : 'inline-flex'} // --- UPDATE (v7.0)
        icon={isSidebarOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        size="md"
        colorScheme="blue"
        position="absolute"
        top="50%"
        right={isSidebarOpen ? `${SIDEBAR_WIDTH_PX - 20}px` : '-20px'}
        transform="translateY(-50%)"
        zIndex={15}
        borderRadius="full"
        boxShadow="2xl"
        borderWidth="4px"
        borderColor={sidebarBg}
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        _hover={{ transform: 'translateY(-50%) scale(1.1)' }}
      />

      {/* ==================== SIDEBAR AREA ==================== */}
      <Box
        as="aside"
        position="absolute"
        right="0"
        w={`${SIDEBAR_WIDTH_PX}px`}
        h="100%"
        bg={sidebarBg}
        borderLeftWidth="1px"
        borderColor={sidebarBorder}
        boxShadow="-10px 0 30px rgba(0,0,0,0.05)"
        zIndex={10}
        transform={isSidebarOpen ? 'translateX(0)' : `translateX(${SIDEBAR_WIDTH_PX}px)`}
        transition="transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        display="flex"
        flexDirection="column"
      >
        <Box p={4} borderBottomWidth="1px" borderColor={sidebarBorder}>
          <Heading size="md" fontWeight="800" color={headerText} display="flex" alignItems="center" gap={2}>
            <Icon as={FaServer} color="blue.500" /> FlowSense Hub
          </Heading>
          {lastUpdateTime && (
            <Text fontSize="xs" color={mutedText} mt={1} fontWeight="600">
              Last Sync: {lastUpdateTime.toLocaleTimeString()}
            </Text>
          )}
        </Box>

        <Tabs isFitted variant="soft-rounded" colorScheme="blue" display="flex" flexDirection="column" flex="1" overflow="hidden" index={sidebarTab} onChange={setSidebarTab}>
          <TabList px={4} pt={2} borderBottomWidth="1px" borderColor={sidebarBorder}>
            <Tab fontWeight="700" fontSize="sm">Inspect</Tab>
            <Tab fontWeight="700" fontSize="sm">Layers</Tab>
            <Tab fontWeight="700" fontSize="sm">
              Tools
              {/* --- UPDATE */}
              {(simulation.isRunning || simulation.result) && <Box as="span" ml={2} w="8px" h="8px" borderRadius="full" bg={simulation.isRunning ? 'purple.400' : 'orange.400'} display="inline-block" />}
              {/* --- END UPDATE */}
            </Tab>
            {/* --- UPDATE (v7.0) */}
            <Tab fontWeight="700" fontSize="sm">
              AI
              {criticalFindings > 0 && <Badge ml={2} colorScheme="red" borderRadius="full" fontSize="2xs">{criticalFindings}</Badge>}
            </Tab>
            {/* --- END UPDATE */}
          </TabList>

          <TabPanels flex="1" overflowY="auto" css={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-track': { width: '6px' },
            '&::-webkit-scrollbar-thumb': { background: 'gray', borderRadius: '24px' },
          }}>

            <TabPanel p={4} display="flex" flexDirection="column" gap={4}>
              {selectedAsset && (
                <Button
                  colorScheme="cyan"
                  leftIcon={<Icon as={FaBrain} />}
                  onClick={() => {
                    setTwinOverlayAsset(selectedAsset);
                    onTwinDrawerOpen();
                  }}
                  boxShadow="0 0 12px rgba(0,212,170,0.3)"
                  _hover={{ boxShadow: "0 0 20px rgba(0,212,170,0.5)", transform: "translateY(-1px)" }}
                  transition="all 0.2s"
                  w="full"
                  color="white"
                  fontWeight="bold"
                >
                  LAUNCH DIGITAL TWIN ENGINE
                </Button>
              )}

              {/* --- UPDATE (v7.0): selected sensor → Sensor Intelligence */}
              {selectedAsset && (selectedAsset.isSensor || selectedAsset.type === 'sensor') && (
                <Button colorScheme="blue" variant="outline" w="full" onClick={() => openSensor(selectedAsset)}>Open Sensor Intelligence</Button>
              )}
              {/* --- END UPDATE */}
              <AssetInformationPanel
                asset={selectedAsset}
                assetHealth={assetHealthData[selectedAsset?.id]}
                issues={issues}
                alerts={alerts}
                sensorReadings={sensorReadings}
                psiConditions={psiConditions}
                onViewFullAsset={setFullDetailAsset}
                isLoading={!isLoaded}
                // --- UPDATE (v7.0): everything the panel can use
                sensors={sensors}
                sensorPipelineLinks={sensorPipelineLinks}
                predictedFailures={predictedFailureList}
                isHistoricalMode={!isLive}
                historicalTimestamp={currentTimestamp}
                onOpenTwin={(assetTarget) => { setTwinOverlayAsset(assetTarget); onTwinDrawerOpen(); }}
                onOpenSensor={openSensor}
                onRefresh={() => fetchAllData({ manual: true })}
                riskScore={selectedAsset ? twinIntel.riskById.get(String(selectedAsset.id)) || null : null}
                intel={twinIntel}
                liveStatus={selectedAsset ? entityLiveStatus[String(selectedAsset.id)] || null : null}
                // --- END UPDATE
              />
              {/* --- UPDATE (elevation): TessaDEM ground elevation, terrain and profile of the inspected asset */}
              {selectedAsset?.id != null && !(selectedAsset.isSensor || selectedAsset.type === 'sensor') && (
                <GroundElevationCard assetId={selectedAsset.id} compact />
              )}
              {/* --- END UPDATE */}
            </TabPanel>

            <TabPanel p={4}>
              <DigitalTwinLayerManager
                layerVisibility={layerVisibility}
                onLayerToggle={handleLayerToggle}
                layerCounts={layerCounts}
              />
            </TabPanel>

            <TabPanel p={4}>
              <VStack spacing={6} align="stretch">
                {/* --- UPDATE: shared simulation state + context */}
                <SimulationPanel
                  assetId={selectedAsset?.id}
                  assetType={selectedAsset?.type || selectedAsset?.asset_type}
                  selectedAsset={selectedAsset}
                  centroid={networkCentroid}
                  simulation={simulation}
                  onRunSimulation={setSimulationMode}
                  onFocusAsset={handleFocusAsset}
                />
                {/* --- END UPDATE */}
                <Divider borderColor={sidebarBorder} />
                <AugmentedRealityButton context={arContext} />{/* --- UPDATE (v7.0): twin context for the AR renderer */}
              </VStack>
            </TabPanel>
            {/* --- UPDATE (v7.0): FlowSense AI */}
            <TabPanel p={4}>
              {/* --- UPDATE (twin v8): live run-off forecast for wells & catch basins */}
              <Box mb={4}>
                <DrainageOutlookCard
                  onFocus={handleFocusAsset}
                  onRunScenario={() => {
                    const sc = SCENARIO_BY_ID.rainfall_runoff;
                    if (!sc) return;
                    setSidebarTab(2);
                    simulation.run(sc, defaultValuesFor(sc), { centroid: networkCentroid, selectedAsset, assetId: selectedAsset?.id ?? null });
                  }}
                />
              </Box>
              {/* --- END UPDATE */}
              <TwinAiExplainer
                intel={twinIntel}
                selectedAsset={selectedAsset}
                selectedSensor={drawerSensor}
                isLive={isLive}
                historicalTimestamp={currentTimestamp}
                simulation={simulationOverlay}
                onAction={handleAiAction}
              />
            </TabPanel>
            {/* --- END UPDATE */}
          </TabPanels>
        </Tabs>
      </Box>

      {/* ==================== GLOBAL OVERLAYS & MODALS ==================== */}
      {!isFocusMode && ( /* --- UPDATE (v7.0): hidden in focus mode */
      <DataOverlay
        assets={assets} issues={issues} alerts={alerts}
        sensorReadings={sensorReadings} isLive={isLive} onGoLive={handleGoLive}
        onMinimizeChange={setIsDataOverlayMinimized}
        // --- UPDATE
        aiRisk={aiRisk}
        simulation={simulationOverlay}
        onOpenSimulation={() => { setIsSidebarOpen(true); setSidebarTab(2); }}
        // --- END UPDATE
      />
      )}

      <AssetDetailModal
        isOpen={!!fullDetailAsset}
        onClose={() => setFullDetailAsset(null)}
        asset={fullDetailAsset}
        liveReadings={sensorReadings}
        sensorPipelineLinks={sensorPipelineLinks}
        // --- UPDATE: reuse page data (no re-download of all issues/sensors) + simulation impact
        issues={issues}
        sensors={sensors}
        healthData={fullDetailAsset ? assetHealthData[fullDetailAsset.id] : null}
        simulationRecord={fullDetailAsset ? simulation.recordsById.get(String(fullDetailAsset.id)) : null}
        simulationTitle={simulation.meta?.title}
        // --- END UPDATE
        onOpenTwin={(assetTarget) => {
          setFullDetailAsset(null);
          setTwinOverlayAsset(assetTarget);
          onTwinDrawerOpen();
        }}
      />

      {/* --- UPDATE (v7.0): Sensor Intelligence drawer (live reading, trend, FlowSense ML) */}
      <SensorInfoDrawer
        sensor={drawerSensor}
        reading={drawerSensorKey ? sensorReadings[drawerSensorKey] : null}
        isOpen={!!drawerSensor}
        onClose={() => setDrawerSensorKey(null)}
        history={drawerSensorKey ? twinIntel.historyOf(drawerSensorKey) : null}
        intelRow={drawerSensorKey ? twinIntel.sensorByKey.get(drawerSensorKey) || null : null}
        psiConditions={psiConditions}
        isLive={isLive}
        historicalTimestamp={currentTimestamp}
        linkedEntity={drawerLinkedEntity}
        onFlyTo={(c) => flyTo(c)}
        onOpenEntity={(e) => { if (e.isPipeline) { handleAiAction({ type: 'pipeline', id: e.id }); } else { setDrawerSensorKey(null); setFullDetailAsset(e); } }}
        onRequestHistory={fetchSensorHistory}
      />
      {/* --- END UPDATE */}

      {/* THE DIGITAL TWIN ENGINE DRAWER */}
      {/* --- UPDATE (twin overlay v6): solid drawer, close button, AI risk row + bulk health passed in */}
      <Drawer
        isOpen={isTwinDrawerOpen}
        placement="right"
        onClose={closeTwinDrawer}
        size="xl"
      >
        <DrawerOverlay backdropFilter="blur(3px)" />
        <DrawerContent bg={pageBg} boxShadow="2xl" maxW={{ base: '100vw', lg: '860px' }}>
          <DigitalTwinOverlay
            selectedAsset={twinOverlayAsset}
            sensors={twinOverlaySensors}
            readings={sensorReadings}
            isLive={isLive}
            historicalTimestamp={currentTimestamp}
            healthHistory={EMPTY_HISTORY}
            onClose={closeTwinDrawer}
            onFocusAsset={focusFromTwinDrawer}
            risk={twinOverlayAsset ? twinIntel.riskById.get(String(twinOverlayAsset.id)) || null : null}
            assetHealth={twinOverlayAsset ? assetHealthData[twinOverlayAsset.id] || null : null}
            // --- UPDATE
            simulationRecord={twinOverlayAsset ? simulation.recordsById.get(String(twinOverlayAsset.id)) : null}
            simulationMeta={simulation.meta}
            simulationDownstreamCount={twinOverlayAsset ? (simDownstreamCounts.get(String(twinOverlayAsset.id)) || 0) : 0}
            // --- END UPDATE
            onCriticalAlarm={handleCriticalAlarm}
          />
        </DrawerContent>
      </Drawer>
      {/* --- END UPDATE */}

    </Flex>
  );
}